import React from 'react';
import type { KeyboardEventCallback, KeyboardShortcut } from './types';
import { isModalOpen, isValidKeyboardEvent } from './helpers';

/**
 * Abstraction to easily register a component with keyboard events. Shortcuts do not fire
 * while a modal is open, since the modal owns the keyboard until it is dismissed.
 * @param keyboardShortcut - keyboard shortcut to listen to
 * @param disabled - whether the keyboard shortcut is disabled; does not register an event listener if true
 */
export function useKeyboardKeyEvent({
  shortcut,
  callback,
  enabled = true,
}: {
  shortcut: KeyboardShortcut;
  callback: KeyboardEventCallback;
  enabled?: boolean;

}) {
  React.useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (isModalOpen()) return;

      if (isValidKeyboardEvent(event, shortcut)) {
        callback(event);
      }
    }

    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (enabled) {
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [shortcut, callback, enabled]);
}
